
import { Dialog, Progress, Title } from "@components";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
    fetchVehiclesWithTags,
    fetchVehicleTags,
} from "src/services/routes";

import {
    setIdVehicleSelected,
    setNameVehicleSelected,
    setTotalByVehicleType,
} from "@store";

import { NavigateState } from "@types";
import { ProgressOptions } from "src/components/Progress/Trip";
import { Notification } from "@rio-cloud/rio-uikit";
import { string } from "zod";

const PAGE_SIZE = 100
export interface IVehicle {
    id: string;
    name: string;
    accountId?: string;
    status?: string;
    type?: string;
    identification?: string;
    identificationType?: string;
    brand: string;
    licensePlate?: string;
    licensePlateCountryCode?: string;
    embedded?: null | string[];
}

const TripsVehicleScreen = () => {
    const [error, setError] = useState("");

    const [vehicles, setVehicles] = useState<IVehicle[]>([]);
    const [searchVehicle, setSearchVehice] = useState("");

    const [selectedVehicleId, setSelectedVehicleId] = useState("");
    const [selectedVehicleName, setSelectedVehicleName] = useState("");

    const [pageVehicle, setPageVehicle] = useState(1);
    const [pagesVehicle, setPagesVehicle] = useState(1);
    const [fleets, setFleets] = useState<{ [x: string]: string; }>();
    const [vehicleTotals, setVehicleTotals] = useState<{ [key: string]: number }>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [totalTagPages, setTotalTagPages] = useState(1);
    const [currentVehicleTypeFilter, setCurrentVehicleTypeFilter] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [searchLoading, setSearchLoading] = useState(false);
    const [showItemsVehicle, setShowItemsVehicle] = useState({
        start: 0,
        end: 10,
    })

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [search] = useSearchParams();

    const location = useLocation();
    const navigateState = location.state as NavigateState | null;

    const currentParams = new URLSearchParams(search);

    const initialVehicleData = async (vehicleType?: string | null, searchText?: string) => {
        setIsLoading(true);
        
        try {
            const tagsRes = await fetchVehicleTags(
                1, 
                PAGE_SIZE, 
                "ASC", 
                "id", 
                undefined, 
                vehicleType || undefined,
                searchText || undefined
            );
            
            if (tagsRes.data?.result) {
                if (tagsRes.data.result.result && Array.isArray(tagsRes.data.result.result)) {
                    const tagsData = tagsRes.data.result.result;
                    
                    if (tagsRes.data.result.totalByVehicleType) {
                        dispatch(setTotalByVehicleType(tagsRes.data.result.totalByVehicleType));
                    }
                    
                    const totals: { [key: string]: number } = {};
                    const fleetsMapping: { [key: string]: string } = {};
                    
                    tagsData.forEach((tag: any) => {
                        totals[tag.tagName.toLowerCase()] = tag.totalVehicles || 0;
                        fleetsMapping[tag.tagId] = tag.tagName;
                    });
                    setVehicleTotals(totals);
                    setFleets(fleetsMapping);
                    
                    const vehicleMap = new Map<string, IVehicle>();
                    tagsData.forEach((tag: any) => {
                        if (tag.vehicles && tag.vehicles.length > 0) {
                            tag.vehicles.forEach((vehicle: any) => {
                                if (!vehicleMap.has(vehicle.id)) {
                                    vehicleMap.set(vehicle.id, {
                                        ...vehicle,
                                        embedded: vehicle.embeddedTags || (tag.tagId === '00000000-0000-0000-0000-000000000000' ? [] : [tag.tagId])
                                    });
                                }
                            });
                        }
                    });
                    const allVehiclesFromTags = Array.from(vehicleMap.values());
                    
                    setVehicles(allVehiclesFromTags);

                    const groupCount = tagsData.length;
                    const calculatedPages = Math.ceil(groupCount / PAGE_SIZE);
                    setPagesVehicle(calculatedPages > 0 ? calculatedPages : 1);
                    
                    if (tagsRes.data.result.totalPages) {
                        setTotalTagPages(tagsRes.data.result.totalPages);
                    }

                    setSearchLoading(false);
                } else if (Array.isArray(tagsRes.data.result)) {
                    const tags = tagsRes.data.result;
                    
                    const fleetsMapping: { [key: string]: string } = {};
                    tags.forEach((tag: any) => {
                        fleetsMapping[tag.id] = tag.name;
                    });
                    setFleets(fleetsMapping);
                    
                    setVehicles([]);
                    setPagesVehicle(1);
                    setSearchLoading(false);
                }
            }
        } catch (error) {
            console.error('[tripsVehicle] Error fetching initial vehicle data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLoadMoreTags = async (): Promise<void> => {
        try {
            const nextPage = currentPage + 1;
            const res = await fetchVehicleTags(
                nextPage, 
                PAGE_SIZE,
                "ASC",
                "id",
                undefined,
                currentVehicleTypeFilter || undefined,
                searchVehicle || undefined
            );
            
            if (res.data?.result) {
                if (res.data.result.result && Array.isArray(res.data.result.result)) {
                    const tagsData = res.data.result.result;
                    
                    const vehicleMap = new Map<string, IVehicle>();
                    const newFleetsMapping: { [key: string]: string } = {};
                    const newTotals: { [key: string]: number } = {};
                    
                    tagsData.forEach((tag: any) => {
                        newTotals[tag.tagName.toLowerCase()] = tag.totalVehicles || 0;
                        newFleetsMapping[tag.tagId] = tag.tagName;
                        
                        if (tag.vehicles && tag.vehicles.length > 0) {
                            tag.vehicles.forEach((vehicle: any) => {
                                if (!vehicleMap.has(vehicle.id)) {
                                    vehicleMap.set(vehicle.id, {
                                        ...vehicle,
                                        embedded: vehicle.embeddedTags || (tag.tagId === '00000000-0000-0000-0000-000000000000' ? [] : [tag.tagId])
                                    });
                                }
                            });
                        }
                    });
                    const newVehiclesFromTags = Array.from(vehicleMap.values());
                    
                    setVehicles(prev => [...prev, ...newVehiclesFromTags]);
                    setFleets(prev => ({...prev, ...newFleetsMapping}));
                    setVehicleTotals(prev => ({...prev, ...newTotals}));
                    setCurrentPage(nextPage);
                }
            }
        } catch (error) {
            console.error('[tripsVehicle] Error fetching more tags:', error);
        }
    };

    const handleFilterByType = async (vehicleType: string | null) => {
        setCurrentVehicleTypeFilter(vehicleType);
        setCurrentPage(1);
        setTotalTagPages(1);
        setVehicles([]);
        setFleets({});
        setVehicleTotals({});
        setPageVehicle(1);
        setPagesVehicle(1);
        setShowItemsVehicle({
            start: 0,
            end: 10,
        });
        await initialVehicleData(vehicleType, searchVehicle);
    };

    const initData = async () => {
        await initialVehicleData(currentVehicleTypeFilter, searchVehicle);
    }

    useEffect(() => {
        initData();
    }, []);

    useEffect(() => {
        const delaySearch = setTimeout(() => {
            setSearchLoading(true);
            setPageVehicle(1);
            setCurrentPage(1);
            initData();
        }, 500);

        return () => clearTimeout(delaySearch);
    }, [searchVehicle]);

    const filteredVehicles = vehicles;


    const setVehicle = () => {
      dispatch(setIdVehicleSelected(selectedVehicleId));
      dispatch(setNameVehicleSelected(selectedVehicleName));
    }

    const handleSaveVehicle = (id: string, name: string) => {
        handleSubmit()
    };

    const onVehicleSelectionChange = (id: string, name: string) => {
      setSelectedVehicleId(id)
      setSelectedVehicleName(name)
    }

    const handleSubmit = () => {
      setVehicle()
      const currentParams = new URLSearchParams(search);
      currentParams.set('trip', 'true');
      const url = currentParams.get('editTrip') ? '/routing/trips/update?' : '/routing/trips/custom?';

      navigate(url + currentParams, {
        state: {
          edit: navigateState?.edit,
          csv: navigateState?.csv,
          csvData: navigateState?.csvData,
        }
      })
    }

    const handlePageChange = async (params: { page: number, pages?: number }) => {
        setPageVehicle(params.page);
        const start = (params.page - 1) * PAGE_SIZE;
        const end = params.page * PAGE_SIZE;

        setShowItemsVehicle({
            start: start,
            end: end,
        });

        setIsLoading(true);
        try {
            await handleLoadMoreTags();
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        const url = currentParams.get('editTrip') ? '/routing/trips/update' : '/routing/trips/driver?' + currentParams.toString();
        navigate(url, {
            state: {
                edit: navigateState?.edit,
                csv: navigateState?.csv
            }
        })
    }


    const [content, setContent] = useState(5);

    useEffect(() => {
      if (error) {
        Notification.error(error);
      }
    }, [error])

    const pageIndex = 2;
    const onContent = (idx: number) => {
      console.log(selectedVehicleId, selectedVehicleName, (selectedVehicleId === "" || selectedVehicleName === "") && idx > pageIndex)
      if ((selectedVehicleId === "" || selectedVehicleName === "") && idx > pageIndex) {
        setError("Selecione o veículo")
        return;
      }

      if (selectedVehicleId && selectedVehicleName)
        setVehicle()

      navigate(ProgressOptions[idx]?.route + '?' + currentParams.toString(), { state: { edit: navigateState?.edit, cvs: navigateState?.csv } })
    }

    return (
        <div className="display-flex flex-column gap-20 padding-y-20 padding-x-50 height-100pct">
            <div>
                <Title
                    icon="rioglyph rioglyph-truck"
                    title="Vincular veículo"
                />
            </div>
            <div>
                <Progress.Trip
                    selected={content}
                    onSelectedChanged={onContent}
                    step={pageIndex}
                    disabledIndex={pageIndex + 1}
                />
            </div>

            <div>
                <Dialog.AddVehicle
                    isLoading={isLoading}
                    searchLoading={searchLoading}
                    pageSize={PAGE_SIZE}
                    initialSlice={showItemsVehicle.start}
                    endSlice={showItemsVehicle.end}
                    inputValue={searchVehicle}
                    onChangeInput={(text) => setSearchVehice(text)}
                    vehicles={filteredVehicles}
                    fleets={fleets}
                    vehicleTotals={vehicleTotals}
                    onClose={() => { }}
                    handleCancel={handleCancel}
                    open={true}
                    handleSaveVehicle={handleSaveVehicle}
                    onVehicleSelectionChange={onVehicleSelectionChange}
                    page={pageVehicle}
                    pages={totalTagPages}
                    handlePage={params => handlePageChange(params)}
                    onLoadMoreTags={currentPage < totalTagPages ? handleLoadMoreTags : undefined}
                    hasMoreTags={currentPage < totalTagPages}
                    onFilterByType={handleFilterByType}
                    currentVehicleTypeFilter={currentVehicleTypeFilter}
                />
            </div>

        </div>
    );
};

export default TripsVehicleScreen;